/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useEffect, useState} from 'react';
import Dialog from '@material-ui/core/Dialog';
import {makeStyles} from '@material-ui/core/styles';
import CloseButton from '../components/buttons/CloseButton';
import {PrimaryButton} from '../components/buttons/PrimaryButton';
import {SecondaryButton} from '../components/buttons/SecondaryButton';
import {TertiaryButton} from '../components/buttons/TertiaryButton';
import TextField from '../components/textfield/TextField';
import Render from '../render/Render';
import {
  dlsColor,
  dlsShadow,
  dlsZIndex
} from '../theme/theme';
import {DLSShadowStyle} from '../types/types';

export interface FilterFavoriteDialogProps {
  cancelLabel: string;
  className?: string;
  description: string;
  errorText?: string;
  initialName?: string;
  inputLabel: string;
  open: boolean;
  placeholder: string;
  primaryLabel: string;
  secondaryLabel?: string;
  title: string;
  zIndex?: number;
  onCancel: () => void;
  onPrimaryAction: (name: string) => void;
  onSecondaryAction?: (name: string) => void;
}

const useStyles = makeStyles(() => ({
  dialogRoot : {
    zIndex : ({zIndex}: FilterFavoriteDialogProps) => (
      `${zIndex ?? dlsZIndex('userNotificationDialogRoot')} !important` as any
    )
  },
  paper : {
    backgroundColor : `${dlsColor('white')} !important`,
    borderRadius    : 12,
    borderTop       : `2px solid ${dlsColor('pastelTeal')}`,
    boxShadow       : dlsShadow(2.1, DLSShadowStyle.Inset),
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    margin          : '25px !important',
    maxWidth        : '540px !important',
    width           : 540
  },
  header : {
    alignItems     : 'center',
    boxSizing      : 'border-box',
    display        : 'flex',
    height         : 56,
    justifyContent : 'space-between',
    padding        : '0 16px',
    width          : '100%'
  },
  title : {
    color      : dlsColor('grey1'),
    fontFamily : 'Inter',
    fontSize   : 16,
    fontWeight : 600,
    lineHeight : '20px'
  },
  description : {
    boxSizing  : 'border-box',
    color      : dlsColor('grey1'),
    fontFamily : 'Inter',
    fontSize   : 14,
    fontWeight : 400,
    lineHeight : '20px',
    minHeight  : 36,
    padding    : '8px 16px',
    width      : '100%'
  },
  fieldWrapper : {
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    height        : 154,
    padding       : 16,
    width         : '100%'
  },
  fieldPanel : {
    backgroundColor : dlsColor('grey13'),
    borderRadius    : 12,
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    gap             : 24,
    padding         : 16,
    width           : '100%'
  },
  required : {
    color : dlsColor('red')
  },
  actions : {
    alignItems     : 'center',
    boxSizing      : 'border-box',
    display        : 'flex',
    gap            : 8,
    height         : 64,
    justifyContent : 'flex-end',
    padding        : '8px 16px 16px',
    width          : '100%'
  },
  // Cancel sits alone at the left, the save actions together at the right ...
  cancelAction : {
    marginRight : 'auto'
  },
  // only as wide as the button it holds - the secondary button's own wrapper
  // fills its parent's width, which squeezed the actions beside it ...
  secondaryActionSlot : {
    flex : '0 0 auto'
  },
  // the secondary button's label keeps its own 8px inset, in sentence case,
  // as designed ...
  secondaryAction : {
    textTransform        : 'none',
    whiteSpace           : 'nowrap',
    '& .MuiButton-label' : {
      padding : '0 8px'
    }
  }
}));

/**
 * Displays a name-entry dialog for filter drawer favorites.
 *
 * ```typescript
 * import FilterFavoriteDialog from '@ion/dls/src/filters/FilterFavoriteDialog';
 *
 * ...
 *
 * <FilterFavoriteDialog open
 *   title='Create Favorite'
 *   inputLabel='Filter Name'
 *   primaryLabel='Create Favorite'
 *   cancelLabel='Cancel'
 *   onPrimaryAction={saveFavorite} />
 * ```
 */
export const FilterFavoriteDialog = (props: FilterFavoriteDialogProps) => {
  const {
    cancelLabel,
    className,
    description,
    errorText,
    initialName = '',
    inputLabel,
    open,
    placeholder,
    primaryLabel,
    secondaryLabel,
    title,
    onCancel,
    onPrimaryAction,
    onSecondaryAction
  } = props;
  const classes         = useStyles(props);
  const [name, setName] = useState(initialName);

  useEffect(() => {
    setName(initialName);
  }, [initialName, open]);

  return (
    <Dialog open={open}
      classes={{
        root  : `${classes.dialogRoot} ${className ?? ''}`,
        paper : classes.paper
      }}>
      <div className={classes.header}>
        <div className={classes.title}>{title}</div>
        <CloseButton onClick={onCancel} />
      </div>
      <div className={classes.description}>{description}</div>
      <div className={classes.fieldWrapper}>
        <div className={classes.fieldPanel}>
          <TextField fullWidth
            autoFocus
            fieldLabel={<>{inputLabel} <span className={classes.required}>*</span></>}
            placeholder={placeholder}
            value={name}
            hasError={!!errorText}
            errorText={errorText}
            onChange={function({target: {value}}) {
              setName(value);
            }} />
        </div>
      </div>
      <div className={classes.actions}>
        <div className={classes.cancelAction}>
          <TertiaryButton onClick={onCancel}>
            {cancelLabel}
          </TertiaryButton>
        </div>
        <Render.If condition={!!secondaryLabel && !!onSecondaryAction}>
          <div className={classes.secondaryActionSlot}>
            <SecondaryButton className={classes.secondaryAction}
              onClick={function() {
                onSecondaryAction?.(name);
              }}>
              {secondaryLabel}
            </SecondaryButton>
          </div>
        </Render.If>
        <PrimaryButton onClick={function() {
          onPrimaryAction(name);
        }}>
          {primaryLabel}
        </PrimaryButton>
      </div>
    </Dialog>
  );
};

export default memo(FilterFavoriteDialog);
